/**
 * productDrawerSlice — transient UI state for the Product Drawer.
 *
 * The drawer lets the user pick a variant + options + extras before
 * committing a `CartItem`. We keep its pending selection here (instead of
 * directly in `cartSlice`) so:
 *   - The cart only ever holds *committed* items.
 *   - The "Save changes" flow (re-opening an existing cart item) has a
 *     single place to track what's edited vs. what's been persisted.
 *   - The drawer survives transient re-renders / route changes without
 *     leaking partial state.
 *
 * NOT persisted to the draft — `serializeDraft` ignores this slice. If the
 * user reloads mid-configuration, they'll see the closed drawer; the cart
 * is unaffected.
 */

import type { StateCreator } from 'zustand'

import type { CartItemId, ProductCode, SalesforceId } from 'models/purchaseFlowRevamp'
import type { PfStoreState } from '../store'

export type ProductDrawerMode = 'new' | 'edit'

export interface ProductDrawerSliceState {
  isOpen: boolean
  mode: ProductDrawerMode
  /** Cart item id when editing; null in 'new' mode. */
  editingCartItemId: CartItemId | null
  /** Shell product currently configured. */
  shellProductId: SalesforceId | null
  /** Currently-selected variant. On a multi-select document group this is the
   *  FIRST ticked document — options, uploads and pricing all read it, so the
   *  rest of the drawer keeps working unchanged. */
  selectedVariantId: SalesforceId | null
  /** Further ticked variants on a multi-select document group, in tick order.
   *  Always empty elsewhere. Commit turns each into its own cart line. */
  additionalVariantIds: SalesforceId[]
  /** Option values keyed by option id — bool / number / string-for-enum. */
  optionValues: Record<SalesforceId, boolean | number | string>
  /** Selected extra-service ids, preserves selection order. */
  selectedExtraIds: SalesforceId[]
  /** Per-extra quantities for NUMBER-kind extras. Keyed by extra id.
   *  BOOLEAN-kind extras are not present here (their count is implicitly 1
   *  when selected). Cleared for an extra when it's deselected. */
  extraQuantities: Record<SalesforceId, number>
  /** Quantity the user intends to add. Always clamped to >= 1. */
  quantity: number
  /** The client's own brief for this line, offered only where
   *  `acceptsProductComment` says so. Commits to `CartItem.note`. */
  note: string
  /** A pack's members the client unticked. Empty means every member is ordered, the default. */
  unselectedPackMemberCodes: ProductCode[]
  /** Options and extras chosen on each pack member, keyed by the member's product code. */
  packMemberAddons: Record<ProductCode, PackMemberAddons>
}

/** What a client picked on one pack member: its add-ons, as a single drawer line would hold them. */
export interface PackMemberAddons {
  optionValues: Record<SalesforceId, boolean | number | string>
  extraIds: SalesforceId[]
  extraQuantities: Record<SalesforceId, number>
}

export const EMPTY_PACK_MEMBER_ADDONS: PackMemberAddons = { optionValues: {}, extraIds: [], extraQuantities: {} }

export interface ProductDrawerSliceActions {
  /**
   * Opens the drawer in 'new' mode for a given shell product.
   *
   * `defaultVariantId` may be null to open with NOTHING selected — authorities
   * documents do that, because pre-ticking one there means hitting Add without
   * reading buys whichever document happens to be cheapest. Everywhere else a
   * default is wanted: those groups are variants of one product (10 photos vs
   * 15), so opening with no pick would just be an extra click.
   */
  openForNew(shellProductId: SalesforceId, defaultVariantId: SalesforceId | null): void
  /** Opens the drawer in 'edit' mode populated from an existing cart item. */
  openForEdit(
    cartItemId: CartItemId,
    snapshot: {
      shellProductId: SalesforceId
      variantId: SalesforceId
      optionValues: Record<SalesforceId, boolean | number | string>
      extraServiceIds: SalesforceId[]
      /** Chosen counts for NUMBER-kind extras. Without these an edit re-prices
       *  the extra at one unit and saves that back. Defaults to {}. */
      extraQuantities?: Record<SalesforceId, number>
      quantity: number
      /** The line's existing note, so an edit does not silently clear it. */
      note?: string
    }
  ): void
  /** Closes the drawer and discards pending selection. */
  close(): void
  setVariant(variantId: SalesforceId): void
  /**
   * Ticks or unticks one document of a multi-select group.
   *
   * Unticking the primary promotes the next ticked document rather than
   * emptying the drawer, so the options/upload/pricing sections never lose
   * their subject while other documents are still ticked.
   *
   * `displacedVariantIds` are dropped in the same update — the caller resolves
   * them through `variantsDisplacedBySelectionGroup`, which only ever returns
   * anything ON selection (legacy's rule: unticking never removes a sibling).
   */
  toggleDocumentVariant(variantId: SalesforceId, displacedVariantIds?: SalesforceId[]): void
  setOptionValue(optionId: SalesforceId, value: boolean | number | string): void
  clearOptionValue(optionId: SalesforceId): void
  toggleExtra(extraId: SalesforceId): void
  setExtraQuantity(extraId: SalesforceId, quantity: number): void
  setQuantity(quantity: number): void
  setNote(note: string): void
  /** Ticks or unticks one member of a pack. */
  togglePackMember(productCode: ProductCode): void
  setPackMemberOption(productCode: ProductCode, optionId: SalesforceId, value: boolean | number | string): void
  clearPackMemberOption(productCode: ProductCode, optionId: SalesforceId): void
  togglePackMemberExtra(productCode: ProductCode, extraId: SalesforceId): void
  setPackMemberExtraQuantity(productCode: ProductCode, extraId: SalesforceId, quantity: number): void
}

export type ProductDrawerSlice = ProductDrawerSliceState & ProductDrawerSliceActions

const emptyState: ProductDrawerSliceState = {
  isOpen: false,
  mode: 'new',
  editingCartItemId: null,
  shellProductId: null,
  selectedVariantId: null,
  additionalVariantIds: [],
  optionValues: {},
  selectedExtraIds: [],
  extraQuantities: {},
  quantity: 1,
  note: '',
  unselectedPackMemberCodes: [],
  packMemberAddons: {},
}

export const createProductDrawerSlice: StateCreator<PfStoreState, [], [], ProductDrawerSlice> = (set) => ({
  ...emptyState,

  openForNew: (shellProductId, defaultVariantId) => set({
    ...emptyState,
    isOpen: true,
    mode: 'new',
    shellProductId,
    selectedVariantId: defaultVariantId,
  }),

  openForEdit: (cartItemId, snapshot) => set({
    ...emptyState,
    isOpen: true,
    mode: 'edit',
    editingCartItemId: cartItemId,
    shellProductId: snapshot.shellProductId,
    selectedVariantId: snapshot.variantId,
    optionValues: { ...snapshot.optionValues },
    selectedExtraIds: [...snapshot.extraServiceIds],
    extraQuantities: { ...(snapshot.extraQuantities ?? {}) },
    quantity: Math.max(1, snapshot.quantity),
    note: snapshot.note ?? '',
  }),

  close: () => set({ ...emptyState }),

  setVariant: (variantId) => set((state) => {
    if (state.selectedVariantId === variantId) return state
    // Changing variant clears option values — different variants have
    // different option definitions and carry-over is always wrong.
    return {
      selectedVariantId: variantId,
      // A radio pick replaces the whole selection, multi-select or not.
      additionalVariantIds: [],
      optionValues: {},
      unselectedPackMemberCodes: [],
      packMemberAddons: {},
    }
  }),

  toggleDocumentVariant: (variantId, displacedVariantIds = []) => set((state) => {
    const ticked = state.selectedVariantId
      ? [state.selectedVariantId, ...state.additionalVariantIds]
      : state.additionalVariantIds

    const next = ticked.includes(variantId)
      ? ticked.filter((id) => id !== variantId)
      : [...ticked.filter((id) => !displacedVariantIds.includes(id)), variantId]

    const [primary = null, ...additional] = next
    return {
      selectedVariantId: primary,
      additionalVariantIds: additional,
      // Options belong to a variant; a changed primary must not inherit them.
      optionValues: primary === state.selectedVariantId ? state.optionValues : {},
    }
  }),

  setOptionValue: (optionId, value) => set((state) => ({
    optionValues: { ...state.optionValues, [optionId]: value },
  })),

  clearOptionValue: (optionId) => set((state) => {
    if (!(optionId in state.optionValues)) return state
    const { [optionId]: _removed, ...rest } = state.optionValues
    return { optionValues: rest }
  }),

  toggleExtra: (extraId) => set((state) => {
    const current = state.selectedExtraIds
    const isSelected = current.includes(extraId)
    if (isSelected) {
      // Deselect — remove from list and clear any quantity entry.
      const { [extraId]: _removed, ...restQty } = state.extraQuantities
      return {
        selectedExtraIds: current.filter((id) => id !== extraId),
        extraQuantities: restQty,
      }
    }
    return { selectedExtraIds: [...current, extraId] }
  }),

  setExtraQuantity: (extraId, quantity) => set((state) => {
    const clamped = Math.max(1, Math.floor(quantity))
    return { extraQuantities: { ...state.extraQuantities, [extraId]: clamped } }
  }),

  setQuantity: (quantity) => set({
    quantity: Math.max(1, Math.floor(quantity)),
  }),

  setNote: (note) => set({ note }),

  togglePackMember: (productCode) => set((state) => ({
    unselectedPackMemberCodes: state.unselectedPackMemberCodes.includes(productCode)
      ? state.unselectedPackMemberCodes.filter((code) => code !== productCode)
      : [...state.unselectedPackMemberCodes, productCode],
  })),

  setPackMemberOption: (productCode, optionId, value) => set((state) => {
    const current = state.packMemberAddons[productCode] ?? EMPTY_PACK_MEMBER_ADDONS
    return { packMemberAddons: { ...state.packMemberAddons, [productCode]: { ...current, optionValues: { ...current.optionValues, [optionId]: value } } } }
  }),

  clearPackMemberOption: (productCode, optionId) => set((state) => {
    const current = state.packMemberAddons[productCode] ?? EMPTY_PACK_MEMBER_ADDONS
    const { [optionId]: _cleared, ...optionValues } = current.optionValues
    return { packMemberAddons: { ...state.packMemberAddons, [productCode]: { ...current, optionValues } } }
  }),

  togglePackMemberExtra: (productCode, extraId) => set((state) => {
    const current = state.packMemberAddons[productCode] ?? EMPTY_PACK_MEMBER_ADDONS
    const isOn = current.extraIds.includes(extraId)
    const { [extraId]: _dropped, ...extraQuantities } = current.extraQuantities
    return {
      packMemberAddons: {
        ...state.packMemberAddons,
        [productCode]: isOn
          ? { ...current, extraIds: current.extraIds.filter((id) => id !== extraId), extraQuantities }
          : { ...current, extraIds: [...current.extraIds, extraId] },
      },
    }
  }),

  setPackMemberExtraQuantity: (productCode, extraId, quantity) => set((state) => {
    const current = state.packMemberAddons[productCode] ?? EMPTY_PACK_MEMBER_ADDONS
    return {
      packMemberAddons: {
        ...state.packMemberAddons,
        [productCode]: { ...current, extraQuantities: { ...current.extraQuantities, [extraId]: Math.max(1, Math.floor(quantity)) } },
      },
    }
  }),
})
